import styled, { css } from 'styled-components'
import { Typography } from '@material-ui/core'
import { rem } from '../../utils'
import { StyledLinkSection } from './StyledSecondaryBanner'

export type HeroAlign = 'left' | 'right'

/* Full-bleed hero section. Spans the viewport while content stays in the
   container. Layers (within the isolated stacking context):
   image (-3) → video (-2, lg+ only) → scrim (-1) → content. */
export const HeroSection = styled.section<{ $align: HeroAlign }>`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: flex;
  align-items: center;
  color: ${props => props.theme.palette.common.white};

  /* Fallback background, mirroring the standard secondary banner's orange
     gradient. The image (z-index:-3) and video (z-index:-2) layers paint above
     this, so it only shows when there's no media; the scrim then darkens it for
     legibility, exactly as it would over a photo. */
  background: linear-gradient(43deg, #E37025 15%, #EBA719 50%);

  /* Full-bleed: break out of any constrained parent to span 100vw */
  width: 100vw;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;

  /* Match the standard secondary banner's height at each breakpoint */
  min-height: ${props => rem(265, props.theme)};

  ${props => props.theme.breakpoints.up('sm')} {
    min-height: ${props => rem(450, props.theme)};
  }
  ${props => props.theme.breakpoints.up('md')} {
    min-height: ${props => rem(580, props.theme)};
    background: linear-gradient(90deg, #D25015 0%, #DD671B 20%, #E58826 40%);
  }
  ${props => props.theme.breakpoints.up('xl')} {
    background: linear-gradient(90deg, #D25015 0%, #DD671B 23%, #E58826 50%);
  }
  ${props => props.theme.breakpoints.up(2200)} {
    min-height: ${props => rem(670, props.theme)};
  }
`

export const HeroImageWrap = styled.div`
  position: absolute;
  inset: 0;
  z-index: -3;

  /* next/legacy/image with layout="fill" renders a positioned span */
  img {
    object-fit: cover;
  }
`

/* Background video — shown on lg+ only; below that the image is the fallback
   (matches the home banner's responsive behaviour). Clean cover-fit: the iframe
   is sized 16:9 from the full-bleed width and centred, so it fills the banner
   with a symmetric crop — no distortion, no letterbox bars. */
export const HeroVideoWrap = styled.div`
  position: absolute;
  inset: 0;
  z-index: -2;
  overflow: hidden;
  display: none;

  ${props => props.theme.breakpoints.up('lg')} {
    display: block;
  }

  iframe {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100vw;
    height: 56.25vw; /* 16:9 of the full-bleed width; taller than the banner → covers */
    border: 0;
    pointer-events: none;
  }
`

const scrimLeft = css`
  background: linear-gradient(90deg,
    rgba(15, 17, 24, 0.72) 0%,
    rgba(15, 17, 24, 0.6) 48%,
    rgba(15, 17, 24, 0.24) 72%,
    transparent 96%);
`

const scrimRight = css`
  background: linear-gradient(270deg,
    rgba(15, 17, 24, 0.72) 0%,
    rgba(15, 17, 24, 0.6) 48%,
    rgba(15, 17, 24, 0.24) 72%,
    transparent 96%);
`

export const HeroScrim = styled.div<{ $align: HeroAlign }>`
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;

  /* Mobile-first: full-frame wash, lightened so brighter photos still clear AA */
  background: linear-gradient(180deg, rgba(15, 17, 24, 0.43) 0%, rgba(15, 17, 24, 0.66) 100%);

  ${props => props.theme.breakpoints.up('sm')} {
    background: linear-gradient(180deg, rgba(15, 17, 24, 0.55) 0%, rgba(15, 17, 24, 0.74) 100%);
  }

  /* Desktop: directional gradient from the text side */
  ${props => props.theme.breakpoints.up('md')} {
    ${props => (props.$align === 'right' ? scrimRight : scrimLeft)}
  }
`

export const HeroInner = styled.div<{ $align: HeroAlign }>`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  padding-block: clamp(2.5rem, 6vw, 4.5rem);

  /* Phones stay left-aligned; from the small breakpoint up the right variant
     pushes the content block to the right edge (matches .fcp-banner--right). */
  ${props => props.theme.breakpoints.up('sm')} {
    justify-content: ${props => (props.$align === 'right' ? 'flex-end' : 'flex-start')};
  }
`

export const HeroContent = styled.div<{ $lightSecondary?: boolean }>`
  max-width: 36rem;
  display: grid;
  gap: ${props => rem(16, props.theme)};
  justify-items: start;
  text-align: left;

  /* Primary CTA matches the home page banner's button (dark red, 4px radius,
     17/18px, and stays dark red on hover rather than flipping to orange). The
     base #CC2605 fill and 16/30 padding come from the shared cta button. */
  ${StyledLinkSection} .MuiButton-containedPrimary {
    border-radius: 4px;
    font-size: ${props => props.theme.typography.pxToRem(17)};

    ${props => props.theme.breakpoints.up('md')} {
      font-size: ${props => props.theme.typography.pxToRem(18)};
    }

    &:hover {
      background-color: #cc2605;
    }
  }

  /* Light secondary button (Secondary Button Type = Light): white text +
     translucent white outline over the photo. Everything else — padding, the
     ::before/::after layout balance, and the chevron-reveal + label-slide hover
     — carries over from the shared black button (hoverStylesBlack), so the light
     variant behaves exactly like the Dark default; only the colours change for
     legibility over the image. The secondary button is the only outlined button
     here (primary is contained), so this scopes cleanly. */
  ${props => props.$lightSecondary && css`
    ${StyledLinkSection} .MuiButton-outlined {
      color: ${props.theme.palette.common.white};
      background-color: transparent;
      border-color: rgba(255, 255, 255, 0.65);

      /* Swap the shared black chevron (chevron_black.svg) for the white one so
         the hover affordance reads over the photo. The size/repeat/opacity and
         the on-hover reveal + slide are inherited from hoverStylesBlack. */
      &::after {
        background-image: url(/images/chevron_white.svg);
      }

      &:hover,
      &:focus {
        color: ${props.theme.palette.common.white};
        background-color: transparent;
        border-color: ${props.theme.palette.common.white};
      }
    }
  `}

  ${props => props.theme.breakpoints.down('xs')} {
    max-width: none;

    /* Full-width buttons on phones (matches the design) */
    ${StyledLinkSection} .MuiButton-root {
      width: 100%;
    }
  }
`

export const HeroTitle = styled(Typography)`
  font-weight: 700;
  line-height: 1.02;
  letter-spacing: -0.025em;
  max-width: 18ch;
  color: ${props => props.theme.palette.common.white};
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.4);
  font-size: clamp(2.4rem, 5.5vw, 4rem);
`

export const HeroText = styled(Typography)`
  font-size: clamp(1rem, 1.5vw, 1.18rem);
  line-height: 1.6;
  max-width: 46ch;
  color: rgba(255, 255, 255, 0.94);
  text-shadow: 0 1px 12px rgba(0, 0, 0, 0.4);
`

/* Keep the standard breadcrumbs but make them legible over the photo */
export const HeroBreadcrumbs = styled.div`
  a,
  p,
  svg,
  .MuiTypography-root {
    color: ${props => props.theme.palette.common.white};
  }

  a:hover p {
    color: ${props => props.theme.palette.common.white};
  }
`
